import { forwardRef } from 'react';
import { Button, Group, Modal, Select, Stack, Tooltip } from '@mantine/core';
import useDelayedOpening from 'src/new_arch/hooks/useDelayedOpening';
import SwitchBox from 'src/components/SwitchBox/SwitchBox';
import UserPreferences from 'src/entities/userPreferences';
import Text from '@ui/components/Text/Text';
import Asset from '../../models/asset';
import Folder from '../../models/folder';
import useDownloadItemsModal from './useDownloadItemsModal';

import './DownloadItemsModal.scss';

export type DownloadItemsModalProps = {
	assets: Asset[];
	folders: Folder[];
	shareRef?: string;
	onClose: ( confirmed: boolean ) => void;
};

const MODAL_STYLES = {
	header: {
		borderBottom: '1px solid var(--color-outline-default)'
	}
}

type SelectItemProps = {
	label: string;
	disabled?: boolean;
	[key: string]: unknown;
};

const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>( ( { label, disabled, ...others }, ref ) => (
	<Tooltip
		label={(
			<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
				The selected asset(s) can only be downloaded in original size.
				{'\n'}
				Low-resolution downloads are not supported for these files.
			</div>
		)}
		disabled={!disabled}
		withArrow
		position="right"
		offset={4}
		withinPortal
		arrowPosition="center"
	>
		<div ref={ref} {...others}>
			<Text variant="body" color={disabled ? 'dark.3' : 'dark.9'}>{label}</Text>
		</div>
	</Tooltip>
) );

const DownloadItemsModal = ( { assets, folders, shareRef, onClose }: DownloadItemsModalProps ) => {
	const opened = useDelayedOpening();

	const {
		appendRoundNumber,
		downloadAllRounds,
		imageSize,
		setAppendRoundNumber,
		setDownloadAllRounds,
		setImageSize,
		isDownloadingMultipleItems,
		showDownloadAllRounds,
		requiresOriginalSize,
		onDownloadClick
	} = useDownloadItemsModal( { assets, folders, shareRef, onClose } );

	const imageSizeOptions = [
		{ value: UserPreferences.assetDownloadsResolutions.original, label: 'Original' },
		{
			value: UserPreferences.assetDownloadsResolutions.lowRes,
			label: 'Low res image',
			disabled: requiresOriginalSize
		}
	];

	return (
		<Modal
			opened={opened}
			onClose={() => onClose( false )}
			title={`Download Item${isDownloadingMultipleItems ? 's' : ''}`}
			centered
			className="DownloadItemsModal"
			styles={MODAL_STYLES}
			size={400}
		>
			<Stack spacing={24} mt={24}>
				{showDownloadAllRounds && (
					<SwitchBox
						data-testid="downloadAllRoundsSwitchBox"
						title="Download All Rounds"
						onSwitch={() => setDownloadAllRounds( !downloadAllRounds )}
						checked={downloadAllRounds}
					/>
				)}
				<SwitchBox
					data-testid="appendRoundNumberSwitchBox"
					title="Append Round Number"
					caption="e.g Filename_r4.jpg"
					onSwitch={() => setAppendRoundNumber( !appendRoundNumber )}
					checked={downloadAllRounds || appendRoundNumber}
					disabled={downloadAllRounds}
				/>
				<Select
					label="Image Size"
					placeholder="Select Image Size"
					data={imageSizeOptions}
					value={imageSize}
					onChange={setImageSize}
					searchable={false}
					itemComponent={SelectItem}
				/>
				<Group position="right" spacing={12}>
					<Button variant="white" color="dark.9" onClick={() => onClose( false )}>Cancel</Button>
					<Button onClick={onDownloadClick}>Download</Button>
				</Group>
			</Stack>
		</Modal>
	);
};

export default DownloadItemsModal;
